/**
 * The Admin's Central teams (D632, doc 25 section 7, owner Q-367) - framework-neutral view-models.
 *
 *  - **Operações › Equipes da Central** (`central_teams`): the teams of the Digital Central per country - a function (service, finance,
 *    commercial, provider registration, safety, supervision), the languages and channels it answers, its weekly business hours, and who
 *    belongs to it. A person can be in many teams. The hours are LOCAL wall-clock windows: the screen shows each served market's own IANA
 *    timezone beside them and whether the team is open there NOW. Membership never widens a grant - the server admits only a person whose
 *    desk grant already reaches the team and says why in words.
 *
 * The console only presents: the server authorizes every call at the team's country (and markets) and validates every write. The checks
 * below mirror the kernel (`@vello/central-teams`) so a draft is refused before the round trip with the SAME stable reason code, which
 * the screen turns into a sentence through `problem.central_team`. Functions, channels, roles, weekdays, statuses, admission reasons and
 * event kinds are codes shown through the vocabulary catalog; neutral: no country, market name, language or timezone is named here.
 */
import type { CentralTeamHoursWindow, CentralTeamPatch, CentralTeamView, OperatingMarketView } from '@vello/api-client';
import {
  TEAM_CHANNELS, TEAM_FUNCTIONS, WEEKDAYS, clockMinutes, isOpenAt, localClock, normalizeChannels, normalizeLanguages, normalizeName, normalizeWeeklyHours, isTeamFunction,
  type HoursWindow, type TeamFunction, type Weekday,
} from '@vello/central-teams';
import { grantsAllow, type KnownGrants } from './console-access.js';
import { isValidTimeZone } from './market-time.js';

/** What the operator may do with teams (the server re-authorizes every call). */
export function centralTeamAuthority(grants: KnownGrants): { readonly mayView: boolean; readonly mayManage: boolean } {
  if (grants === undefined) return { mayView: false, mayManage: false };
  return {
    mayView: grantsAllow(grants, { permission: 'central_team:view', target: 'GEO' }),
    mayManage: grantsAllow(grants, { permission: 'central_team:manage', target: 'GEO' }),
  };
}

/** The functions an operator can pick, in catalog order; the weekdays Monday first; the channels in catalog order. */
export const TEAM_FUNCTION_CHOICES: readonly string[] = TEAM_FUNCTIONS;
export const TEAM_CHANNEL_CHOICES: readonly string[] = TEAM_CHANNELS;
export const TEAM_WEEKDAYS: readonly string[] = WEEKDAYS;

/** The team's function always runs 24 h (the safety team): its hours cannot be turned into windows. */
export const functionIsAlwaysOpen = (fn: string): boolean => fn === 'seguranca';
/** The team's function answers no contact channel (the supervisors' own team). */
export const functionHasNoChannels = (fn: string): boolean => fn === 'supervisao';

export type TeamTone = 'neutral' | 'success' | 'warning' | 'danger';
export const teamStatusTone = (status: string): TeamTone => (status === 'ACTIVE' ? 'success' : 'neutral');

/**
 * D850 (L5-008): a team with nobody in it still receives routed conversations on paper, so the Admin says so. `empty_24h` is the red case - a team that is
 * open all day (safety) and has nobody: an emergency would wait on it while its 30-second deadline runs out. An archived team is not asked.
 */
export type TeamStaffing = 'staffed' | 'empty' | 'empty_24h';
export function teamStaffing(team: Pick<CentralTeamView, 'status' | 'alwaysOpen'> & { readonly memberCount: number }): TeamStaffing {
  if (team.status !== 'ACTIVE' || team.memberCount > 0) return 'staffed';
  return team.alwaysOpen ? 'empty_24h' : 'empty';
}
export const staffingTone = (s: TeamStaffing): TeamTone => (s === 'empty_24h' ? 'danger' : s === 'empty' ? 'warning' : 'success');

/** The active teams with nobody in them, and how many of those run 24 h (for the banner above the list). */
export function emptyTeamsSummary(teams: readonly (Pick<CentralTeamView, 'status' | 'alwaysOpen'> & { readonly memberCount: number })[]): { readonly empty: number; readonly empty24h: number } {
  const states = teams.map(teamStaffing);
  return { empty: states.filter((s) => s !== 'staffed').length, empty24h: states.filter((s) => s === 'empty_24h').length };
}

/** The name shown for a team: the operator-written name when there is one, else the function's label (passed in, from the vocabulary). */
export const teamDisplayName = (team: Pick<CentralTeamView, 'nameOverride'>, functionLabel: string): string => (team.nameOverride !== undefined && team.nameOverride !== '' ? team.nameOverride : functionLabel);

// ---- hours -------------------------------------------------------------------------------------------------------

export interface HoursGroup {
  /** Consecutive weekdays (Monday first) that share exactly the same windows. */
  readonly days: readonly string[];
  readonly windows: readonly { readonly start: string; readonly end: string }[];
}

/**
 * The weekly windows grouped for reading: consecutive days with identical windows become one line ("Mon-Fri 08:00-18:00"). The screen
 * names the days through `central_teams.weekday_short`. A team open 24 h has no windows and no groups.
 */
export function groupHours(hours: readonly CentralTeamHoursWindow[]): readonly HoursGroup[] {
  const byDay = new Map<string, { start: string; end: string }[]>();
  for (const w of hours) byDay.set(w.day, [...(byDay.get(w.day) ?? []), { start: w.start, end: w.end }]);
  const sig = (day: string): string => JSON.stringify(byDay.get(day) ?? []);
  const groups: { days: string[]; windows: { start: string; end: string }[] }[] = [];
  for (const day of WEEKDAYS) {
    const windows = byDay.get(day);
    if (windows === undefined) continue;
    const last = groups[groups.length - 1];
    const previous = WEEKDAYS[WEEKDAYS.indexOf(day) - 1];
    if (last !== undefined && previous !== undefined && last.days[last.days.length - 1] === previous && JSON.stringify(last.windows) === sig(day)) last.days.push(day);
    else groups.push({ days: [day], windows });
  }
  return groups;
}

/** `08:00-12:00, 14:00-18:00` - the windows of one group as text (clock times, not words). */
export const windowsText = (windows: HoursGroup['windows']): string => windows.map((w) => `${w.start}–${w.end}`).join(', ');

// ---- the markets a team serves and whether it is open there now ----------------------------------------------------

export interface ServedMarket {
  readonly id: string;
  readonly name: string;
  readonly timeZone: string | undefined;
}

/**
 * The markets the team serves: its subset, or - for a whole-country team - every registered market of the country that is not
 * retired. Each carries its IANA timezone (undefined when the catalog lists no valid one).
 */
export function servedMarkets(team: Pick<CentralTeamView, 'countryCode' | 'marketIds'>, markets: readonly OperatingMarketView[]): readonly ServedMarket[] {
  const country = team.countryCode.toUpperCase();
  const pool = markets.filter((m) => m.countryCode.toUpperCase() === country && m.status !== 'RETIRED');
  const chosen = team.marketIds.length === 0 ? pool : team.marketIds.map((id) => pool.find((m) => m.id === id) ?? markets.find((m) => m.id === id)).filter((m): m is OperatingMarketView => m !== undefined);
  return chosen.map((m) => ({ id: m.id, name: m.name, timeZone: isValidTimeZone(m.timezone) ? m.timezone : undefined }));
}

export interface MarketOpenNow {
  readonly market: ServedMarket;
  /** True/false on the market's own clock; undefined when the market's timezone is unknown (nothing is claimed). */
  readonly open: boolean | undefined;
  /** The market's local weekday and `HH:MM` right now, for the "it is 14:05 there" line. */
  readonly localTime: string | undefined;
}

/** Whether the team is open right now in each market it serves, each on its own clock (a country can span timezones). */
export function openNowByMarket(team: Pick<CentralTeamView, 'alwaysOpen' | 'weeklyHours' | 'countryCode' | 'marketIds'>, markets: readonly OperatingMarketView[], nowUtc: string): readonly MarketOpenNow[] {
  return servedMarkets(team, markets).map((market) => {
    if (market.timeZone === undefined) return { market, open: team.alwaysOpen ? true : undefined, localTime: undefined };
    const clock = localClock(nowUtc, market.timeZone);
    const localTime = clock === undefined ? undefined : `${String(Math.floor(clock.minute / 60)).padStart(2, '0')}:${String(clock.minute % 60).padStart(2, '0')}`;
    return { market, open: isOpenAt({ alwaysOpen: team.alwaysOpen, weeklyHours: team.weeklyHours as readonly HoursWindow[] }, nowUtc, market.timeZone), localTime };
  });
}

/** How many of the served markets the team is open in right now: `{ open, known }` (`known` excludes a market with no valid timezone). */
export function openNowSummary(now: readonly MarketOpenNow[]): { readonly open: number; readonly known: number; readonly total: number } {
  const known = now.filter((m) => m.open !== undefined);
  return { open: known.filter((m) => m.open === true).length, known: known.length, total: now.length };
}

/** The distinct valid timezones among the served markets (one line when they share a zone, several when the country spans zones). */
export const distinctTimeZones = (markets: readonly ServedMarket[]): readonly string[] => [...new Set(markets.map((m) => m.timeZone).filter((z): z is string => z !== undefined))].sort();

// ---- the editor draft ---------------------------------------------------------------------------------------------

export interface TeamDraft {
  readonly alwaysOpen: boolean;
  readonly windows: readonly CentralTeamHoursWindow[];
  /** Comma- or space-separated language tags as typed (`pt, en`). */
  readonly languagesText: string;
  readonly channels: readonly string[];
  readonly nameOverride: string;
  readonly marketIds: readonly string[];
}

export function draftFromTeam(team: CentralTeamView): TeamDraft {
  return {
    alwaysOpen: team.alwaysOpen,
    windows: team.weeklyHours,
    languagesText: team.languages.join(', '),
    channels: team.channels,
    nameOverride: team.nameOverride ?? '',
    marketIds: team.marketIds,
  };
}

/** The standard business hours of a new team: Monday to Friday 08:00-18:00 and Saturday 08:00-12:00 (local), the owner's default. */
export const STANDARD_WINDOWS: readonly CentralTeamHoursWindow[] = [
  ...(['MON', 'TUE', 'WED', 'THU', 'FRI'] as const).map((day): CentralTeamHoursWindow => ({ day, start: '08:00', end: '18:00' })),
  { day: 'SAT', start: '08:00', end: '12:00' },
];

/** A draft for a new team of a function: the function's own defaults (the safety team 24 h, the supervisors no channel). */
export function newTeamDraft(fn: string, countryLanguages: readonly string[]): TeamDraft {
  const always = functionIsAlwaysOpen(fn);
  return {
    alwaysOpen: always,
    windows: always ? [] : STANDARD_WINDOWS,
    languagesText: countryLanguages.join(', '),
    channels: functionHasNoChannels(fn) ? [] : fn === 'atendimento' || always ? TEAM_CHANNELS : TEAM_CHANNELS.filter((c) => c !== 'voice'),
    nameOverride: '',
    marketIds: [],
  };
}

/** The language tags typed in a field: split on commas and spaces, empty pieces dropped. */
export const parseLanguages = (text: string): readonly string[] => text.split(/[\s,;]+/).map((t) => t.trim()).filter((t) => t !== '');

/**
 * The first stable reason code that refuses the draft (the same codes the server answers with), or undefined when it is valid for
 * a team of function `fn`. Run before the round trip so the operator reads the sentence at once.
 */
export function draftIssue(draft: TeamDraft, fn: string): string | undefined {
  if (!isTeamFunction(fn)) return 'TEAM_FUNCTION_INVALID';
  const languages = normalizeLanguages(parseLanguages(draft.languagesText));
  if (!languages.ok) return languages.code;
  const channels = normalizeChannels(fn, draft.channels);
  if (!channels.ok) return channels.code;
  if (functionIsAlwaysOpen(fn) && !draft.alwaysOpen) return 'TEAM_ALWAYS_OPEN_REQUIRED';
  const hours = normalizeWeeklyHours(draft.alwaysOpen ? [] : draft.windows, draft.alwaysOpen);
  if (!hours.ok) return hours.code;
  const name = normalizeName(draft.nameOverride);
  if (!name.ok) return name.code;
  return undefined;
}

const sameList = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((x, i) => x === b[i]);
const sameWindows = (a: readonly CentralTeamHoursWindow[], b: readonly CentralTeamHoursWindow[]): boolean => a.length === b.length && a.every((w, i) => w.day === b[i]?.day && w.start === b[i]?.start && w.end === b[i]?.end);

/**
 * The patch that turns `team` into the draft: only the fields that really differ (so an untouched field is never sent, and a draft equal to
 * the team is an empty patch the Save button reads as "nothing to save").
 */
export function draftPatch(team: CentralTeamView, draft: TeamDraft): CentralTeamPatch {
  const patch: { -readonly [K in keyof CentralTeamPatch]: CentralTeamPatch[K] } = {};
  const languages = parseLanguages(draft.languagesText);
  if (!sameList(languages, team.languages)) patch.languages = languages;
  const channels = TEAM_CHANNELS.filter((c) => draft.channels.includes(c));
  if (!sameList(channels, team.channels)) patch.channels = channels;
  const hours = normalizeWeeklyHours(draft.alwaysOpen ? [] : draft.windows, draft.alwaysOpen);
  const nextWindows = hours.ok ? hours.hours : draft.windows;
  if (draft.alwaysOpen !== team.alwaysOpen || !sameWindows(nextWindows, team.weeklyHours)) {
    patch.alwaysOpen = draft.alwaysOpen;
    patch.weeklyHours = nextWindows;
  }
  const name = draft.nameOverride.trim();
  if (name !== (team.nameOverride ?? '')) patch.nameOverride = name === '' ? null : name;
  if (!sameList([...draft.marketIds].sort(), team.marketIds)) patch.marketIds = [...draft.marketIds].sort();
  return patch;
}

export const hasChanges = (patch: CentralTeamPatch): boolean => Object.keys(patch).length > 0;

const clock = (minutes: number): string => (minutes >= 1440 ? '24:00' : `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`);

/**
 * A window added to a day: the standard 08:00-18:00 when the day has none, else the hour after the day's last window ends (capped at 24:00).
 * A day whose last window already ends at 24:00 takes no more (the list is returned as it was).
 */
export function addWindow(windows: readonly CentralTeamHoursWindow[], day: string): readonly CentralTeamHoursWindow[] {
  const mine = windows.filter((w) => w.day === day);
  if (mine.length === 0) return sortWindows([...windows, { day, start: '08:00', end: '18:00' }]);
  const lastEnd = Math.max(...mine.map((w) => clockMinutes(w.end, true) ?? 0));
  if (lastEnd >= 1440) return windows;
  return sortWindows([...windows, { day, start: clock(lastEnd), end: clock(Math.min(lastEnd + 60, 1440)) }]);
}

export const sortWindows = (windows: readonly CentralTeamHoursWindow[]): readonly CentralTeamHoursWindow[] =>
  [...windows].sort((a, b) => WEEKDAYS.indexOf(a.day as Weekday) - WEEKDAYS.indexOf(b.day as Weekday) || a.start.localeCompare(b.start));

/** Change the `index`-th window of the draft (`day`'s windows are addressed by their position among ALL windows). */
export const replaceWindow = (windows: readonly CentralTeamHoursWindow[], index: number, next: CentralTeamHoursWindow): readonly CentralTeamHoursWindow[] => windows.map((w, i) => (i === index ? next : w));
export const removeWindow = (windows: readonly CentralTeamHoursWindow[], index: number): readonly CentralTeamHoursWindow[] => windows.filter((_, i) => i !== index);

/** The function of a team as a typed value when it is one the console knows (an unknown future function reads as the uncatalogued value). */
export const knownFunction = (fn: string): TeamFunction | undefined => (isTeamFunction(fn) ? fn : undefined);

/** The countries to offer in a filter or a new team: those of the markets the operator can see, plus those of the loaded teams, sorted. */
export function teamCountries(markets: readonly OperatingMarketView[], teams: readonly Pick<CentralTeamView, 'countryCode'>[]): readonly string[] {
  return [...new Set([...markets.filter((m) => m.status !== 'RETIRED').map((m) => m.countryCode.toUpperCase()), ...teams.map((t) => t.countryCode.toUpperCase())])].sort();
}

/** The registered, non-retired markets of a country (the choices of a team's market subset). */
export const teamMarketsOfCountry = (markets: readonly OperatingMarketView[], countryCode: string): readonly OperatingMarketView[] =>
  markets.filter((m) => m.countryCode.toUpperCase() === countryCode.toUpperCase() && m.status !== 'RETIRED');
